import { authFetch } from '../utils/authFetch';
import { formatChatTimestamp } from '../utils/supportChatTime';
import React, { useEffect, useMemo, useRef, useState } from 'react';

function shortTicketLabel(id) {
  if (!id) return '';
  return `T-${String(id).slice(-6).toUpperCase()}`;
}

function formatTimeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

function deriveStatus(t) {
  if (!t) return 'new';
  if (t.status === 'resolved') return 'resolved';
  return t.hasSupportReply ? 'pending' : 'new';
}

// --- Presentational sub-components (defined at module scope so React keeps
// the same component type across parent re-renders; otherwise the chat
// input inside TicketRow would be remounted on every poll tick and lose
// focus after every keystroke).

function StatusBadge({ s }) {
  const classes = s === 'resolved'
    ? 'bg-green-50 border-green-200 text-green-800'
    : s === 'pending'
      ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
      : 'bg-blue-50 border-blue-200 text-blue-800';
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide ${classes}`}>
      {s}
    </span>
  );
}

function Bubble({ m, documents, onOpenSopDocument }) {
  const mine = m.authorType === 'staff';
  const sopDoc = m.sopDocId ? (documents || []).find((d) => d.id === m.sopDocId) : null;
  const openSop = () => {
    if (sopDoc && typeof onOpenSopDocument === 'function') onOpenSopDocument(sopDoc);
  };
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div className={`${mine ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-900'} rounded-2xl px-3 py-2 max-w-[85%] shadow-sm`}>
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <span className="text-[11px] opacity-80 truncate">{mine ? (m.authorName || 'Staff') : (m.authorName || 'Support')}</span>
          {m.createdAt && (
            <time
              className={`text-[10px] shrink-0 tabular-nums ${mine ? 'text-white/65' : 'text-gray-500'}`}
              dateTime={m.createdAt}
              title={new Date(m.createdAt).toLocaleString()}
            >
              {formatChatTimestamp(m.createdAt)}
            </time>
          )}
        </div>
        {m.text && <div className="text-sm whitespace-pre-wrap">{m.text}</div>}
        {m.sopDocId && (
          <button
            type="button"
            onClick={openSop}
            disabled={!sopDoc}
            className={`mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border transition ${mine ? 'bg-white text-teal-700 border-white hover:bg-teal-50' : 'bg-white text-teal-700 border-teal-300 hover:bg-teal-50'} ${!sopDoc ? 'opacity-60 cursor-not-allowed' : ''}`}
            title={sopDoc ? 'Open SOP' : 'SOP not available'}
          >
            <span>📎</span>
            <span>{m.sopName || sopDoc?.name || 'Open SOP'}</span>
          </button>
        )}
      </div>
    </div>
  );
}

function TicketRow({
  t,
  isNew,
  expanded,
  onToggle,
  expandedLoading,
  expandedMessages,
  expandedDraft,
  onChangeDraft,
  onSendReply,
  documents,
  onOpenSopDocument,
}) {
  const derived = deriveStatus(t);
  // Prefer the human-friendly shortId ("Meevo1") when the server
  // assigned one; fall back to the truncated Firestore doc id so
  // older tickets created before short ids still render.
  const label = t.shortId || shortTicketLabel(t.id);
  const messagesScrollRef = useRef(null);
  // Jump to the newest message whenever this ticket opens or receives
  // new messages. rAF guarantees the container has been laid out; on
  // expand, the ref is `null` before the element mounts so we have to
  // wait for the next frame anyway.
  useEffect(() => {
    if (!expanded) return undefined;
    const raf = requestAnimationFrame(() => {
      const el = messagesScrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(raf);
  }, [expanded, expandedMessages.length]);
  return (
    <div className={`text-sm ${isNew ? 'bg-amber-50' : 'bg-white'}`}>
      <button
        onClick={() => onToggle(t)}
        className="w-full text-left p-3 hover:bg-gray-50 flex items-start gap-2"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold truncate">{t.title || t.categoryNameSnapshot || t.categoryId || 'Uncategorized'}</span>
            <span className="text-[10px] font-mono text-gray-500 shrink-0">{label}</span>
            {isNew && <span className="w-2 h-2 rounded-full bg-red-500" title="New activity" />}
          </div>
          <div className="text-xs text-gray-600 truncate mt-0.5">{t.lastMessagePreview || '—'}</div>
          <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-2">
            <StatusBadge s={derived} />
            <span>Updated {formatTimeAgo(t.lastActivityAt || t.createdAt)}</span>
          </div>
        </div>
        <svg className={`h-4 w-4 mt-1 text-gray-400 transform transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && (
        <div className="px-3 pb-3">
          <div ref={messagesScrollRef} className="rounded-lg border bg-white p-2 max-h-60 overflow-auto space-y-2">
            {expandedLoading && expandedMessages.length === 0 && (
              <div className="text-xs text-gray-500">Loading…</div>
            )}
            {!expandedLoading && expandedMessages.length === 0 && (
              <div className="text-xs text-gray-500">No messages yet.</div>
            )}
            {expandedMessages.map((m) => (
              <Bubble key={m.id} m={m} documents={documents} onOpenSopDocument={onOpenSopDocument} />
            ))}
          </div>
          {t.status !== 'resolved' && (
            <div className="flex gap-2 mt-2">
              <input
                value={expandedDraft}
                onChange={(e) => onChangeDraft(e.target.value)}
                placeholder="Add to this thread…"
                className="flex-1 p-2 border rounded-lg bg-white text-sm"
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSendReply(t); } }}
              />
              <button
                onClick={() => onSendReply(t)}
                disabled={!expandedDraft.trim()}
                className="bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 text-white text-sm px-3 rounded-lg font-semibold"
              >
                Send
              </button>
            </div>
          )}
          {t.status === 'resolved' && (
            <div className="text-[11px] text-gray-500 mt-2">This ticket is resolved. Open Remote Support to file a new one if needed.</div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Stand-alone drawer dedicated to front-desk ticket follow-up.
 * Grouped by employee name. Each ticket row can be expanded to show the
 * full conversation and add a reply.
 */
export default function TicketsDrawer({
  open,
  onClose,
  tickets,
  documents,
  unreadSeenAt,
  ticketSeenMap,
  onMarkTicketSeen,
  onMarkAllSeen,
  onOpenSopDocument,
  staffNameHint,
}) {
  const drawerRef = useRef(null);
  const [expandedTicketId, setExpandedTicketId] = useState('');
  const [expandedMessages, setExpandedMessages] = useState([]);
  const [expandedDraft, setExpandedDraft] = useState('');
  const [expandedLoading, setExpandedLoading] = useState(false);
  const [error, setError] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const [resolvedArchiveCollapsed, setResolvedArchiveCollapsed] = useState(true);
  const [resolvedSearch, setResolvedSearch] = useState('');

  // The archive is always collapsed when the drawer (re)opens, per
  // product requirement. If the FD expands it mid-session, fine, but
  // closing and reopening resets it to collapsed.
  useEffect(() => {
    if (open) {
      setResolvedArchiveCollapsed(true);
      setResolvedSearch('');
    }
  }, [open]);

  const toggleGroup = (key) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleToggleExpand = (ticket) => {
    if (!ticket) return;
    const nextId = expandedTicketId === ticket.id ? '' : ticket.id;
    setExpandedTicketId(nextId);
    if (nextId) {
      if (typeof onMarkTicketSeen === 'function') onMarkTicketSeen(ticket.id);
    }
  };

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === 'Escape') onClose?.();
    }
    if (open) window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  // Poll messages for the currently expanded ticket.
  useEffect(() => {
    if (!open) return undefined;
    if (!expandedTicketId) { setExpandedMessages([]); return undefined; }
    let alive = true;
    const load = async () => {
      try {
        const res = await authFetch(`/api/support/messages?ticketId=${encodeURIComponent(expandedTicketId)}&limit=300`);
        const j = await res.json();
        if (!alive) return;
        if (res.ok && j.ok) setExpandedMessages(Array.isArray(j.messages) ? j.messages : []);
      } catch {
        // ignore
      }
    };
    setExpandedLoading(true);
    load().finally(() => { if (alive) setExpandedLoading(false); });
    const t = setInterval(load, 3000);
    return () => { alive = false; clearInterval(t); };
  }, [open, expandedTicketId]);

  const ticketsList = useMemo(() => (Array.isArray(tickets) ? tickets : []), [tickets]);

  // Every resolved ticket lives in the collapsed "Resolved Tickets"
  // archive at the bottom of the drawer, regardless of whether the front
  // desk has opened it yet. This keeps the employee groups focused on
  // active work only. New resolution activity still drives the global
  // unread count / tab-title flash so the front desk knows to look.
  const { activeTickets, archivedResolved } = useMemo(() => {
    const active = [];
    const archived = [];
    ticketsList.forEach((t) => {
      if (deriveStatus(t) === 'resolved') archived.push(t);
      else active.push(t);
    });
    archived.sort((a, b) => String(b.lastActivityAt || '').localeCompare(String(a.lastActivityAt || '')));
    return { activeTickets: active, archivedResolved: archived };
  }, [ticketsList]);

  // Apply the resolved-archive search filter. An empty query shows
  // everything. Search covers category name, last-message preview,
  // initial message, employee name, and the ticket id suffix so FD
  // can locate past tickets by any of the identifiers they remember.
  const visibleArchivedResolved = useMemo(() => {
    const q = resolvedSearch.trim().toLowerCase();
    if (!q) return archivedResolved;
    return archivedResolved.filter((t) => {
      const fields = [
        t.shortId,
        t.title,
        t.categoryNameSnapshot,
        t.categoryId,
        t.lastMessagePreview,
        t.initialMessage,
        t.createdByName,
        t.id,
      ];
      return fields.some((v) => v && String(v).toLowerCase().includes(q));
    });
  }, [resolvedSearch, archivedResolved]);

  const groupedByEmployee = useMemo(() => {
    const groups = new Map();
    activeTickets.forEach((t) => {
      const key = (t.createdByName || 'Unknown').trim() || 'Unknown';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(t);
    });
    const rank = { new: 0, pending: 1, resolved: 2 };
    groups.forEach((arr) => {
      arr.sort((a, b) => {
        const ra = rank[deriveStatus(a)] ?? 3;
        const rb = rank[deriveStatus(b)] ?? 3;
        if (ra !== rb) return ra - rb;
        return String(b.lastActivityAt || '').localeCompare(String(a.lastActivityAt || ''));
      });
    });
    const hint = (staffNameHint || '').trim().toLowerCase();
    return Array.from(groups.entries()).sort(([a], [b]) => {
      if (hint) {
        const aMine = a.toLowerCase() === hint;
        const bMine = b.toLowerCase() === hint;
        if (aMine && !bMine) return -1;
        if (bMine && !aMine) return 1;
      }
      return a.localeCompare(b);
    });
  }, [activeTickets, staffNameHint]);

  const unseenTicketIds = useMemo(() => {
    if (!ticketsList.length) return new Set();
    const globalSeen = unreadSeenAt || new Date().toISOString();
    const seenMap = ticketSeenMap || {};
    return new Set(
      ticketsList
        .filter((t) => {
          // Only highlight tickets where the MOST RECENT activity came
          // from support. If the FD was the last one to speak (even if
          // an admin had previously replied), there's nothing new to
          // alert them about.
          if (t.lastAuthorType !== 'support') return false;
          if (!t.hasSupportReply) return false;
          const last = t.lastActivityAt || t.createdAt || '';
          if (!last) return false;
          const seenAt = seenMap[t.id] || globalSeen;
          return last > seenAt;
        })
        .map((t) => t.id)
    );
  }, [ticketsList, unreadSeenAt, ticketSeenMap]);

  async function handleSendReply(ticket) {
    const text = expandedDraft.trim();
    if (!text || !ticket) return;
    setError('');
    setExpandedDraft('');
    try {
      const res = await authFetch(`/api/support/messages?ticketId=${encodeURIComponent(ticket.id)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          authorType: 'staff',
          authorName: ticket.createdByName || staffNameHint || 'Staff',
          text,
        }),
      });
      const j = await res.json();
      if (!res.ok || !j.ok) throw new Error(j.error || 'Failed to send');
      // Optimistic append; next poll will replace with the canonical list.
      setExpandedMessages((prev) => [...prev, {
        id: `local-${Date.now()}`,
        createdAt: new Date().toISOString(),
        authorType: 'staff',
        authorName: ticket.createdByName || staffNameHint || 'Staff',
        text,
      }]);
    } catch (e) {
      setError(e.message || String(e));
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90]">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => onClose?.()}
        aria-label="Close tickets drawer overlay"
      />
      <div
        ref={drawerRef}
        className="absolute right-0 top-0 h-full w-full max-w-md bg-white text-gray-900 shadow-2xl border-l flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label="Tickets drawer"
      >
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-500">Tickets</div>
            <div className="text-xs text-gray-600 mt-0.5">
              Grouped by employee • {activeTickets.length} active
              {archivedResolved.length > 0 && <span className="ml-1 text-gray-400">• {archivedResolved.length} archived</span>}
              {unseenTicketIds.size > 0 && <span className="ml-2 text-red-600 font-semibold">{unseenTicketIds.size} updated</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {groupedByEmployee.length > 1 && (
              <button
                onClick={() => {
                  const allCollapsed = groupedByEmployee.every(([name]) => collapsedGroups.has(name));
                  if (allCollapsed) {
                    setCollapsedGroups(new Set());
                  } else {
                    setCollapsedGroups(new Set(groupedByEmployee.map(([name]) => name)));
                  }
                }}
                className="text-[11px] text-gray-600 hover:text-gray-900 underline"
              >
                {groupedByEmployee.every(([name]) => collapsedGroups.has(name)) ? 'Expand all' : 'Collapse all'}
              </button>
            )}
            {unseenTicketIds.size > 0 && (
              <button
                onClick={() => onMarkAllSeen?.()}
                className="text-[11px] text-gray-600 hover:text-gray-900 underline"
              >
                Mark all read
              </button>
            )}
            <button onClick={() => onClose?.()} className="p-2 rounded-full hover:bg-gray-100" aria-label="Close">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-3 space-y-4 overflow-auto flex-1">
          {error && <div className="text-xs text-red-600">{error}</div>}

          {activeTickets.length === 0 && archivedResolved.length === 0 && (
            <div className="text-sm text-gray-500 p-4 text-center">
              No tickets yet. When you submit a support request from Remote Support, it will appear here for follow-up.
            </div>
          )}

          {groupedByEmployee.map(([employeeName, list]) => {
            const mine = staffNameHint && employeeName.toLowerCase() === staffNameHint.toLowerCase();
            const groupUnseen = list.filter((t) => unseenTicketIds.has(t.id)).length;
            const collapsed = collapsedGroups.has(employeeName);
            return (
              <div key={employeeName} className={`rounded-xl border overflow-hidden ${mine ? 'border-teal-200' : 'border-gray-200'}`}>
                <button
                  type="button"
                  onClick={() => toggleGroup(employeeName)}
                  aria-expanded={!collapsed}
                  className={`w-full px-3 py-2 ${collapsed ? '' : 'border-b'} text-sm font-semibold flex items-center justify-between text-left ${mine ? 'bg-teal-50 text-teal-900 hover:bg-teal-100' : 'bg-gray-50 text-gray-800 hover:bg-gray-100'}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <svg
                      className={`h-4 w-4 shrink-0 text-current transform transition-transform ${collapsed ? '-rotate-90' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                    <span className="truncate">{employeeName}</span>
                    {mine && <span className="text-[10px] uppercase tracking-wide bg-teal-600 text-white px-1.5 py-0.5 rounded-full">You</span>}
                    <span className="text-[11px] font-normal text-gray-500">{list.length}</span>
                  </div>
                  {groupUnseen > 0 && (
                    <span className="text-[10px] font-bold uppercase tracking-wide bg-red-600 text-white px-1.5 py-0.5 rounded-full ml-2 shrink-0">
                      {groupUnseen} new
                    </span>
                  )}
                </button>
                {!collapsed && (
                  <div className="divide-y">
                    {list.map((t) => (
                      <TicketRow
                        key={t.id}
                        t={t}
                        isNew={unseenTicketIds.has(t.id)}
                        expanded={expandedTicketId === t.id}
                        onToggle={handleToggleExpand}
                        expandedLoading={expandedTicketId === t.id ? expandedLoading : false}
                        expandedMessages={expandedTicketId === t.id ? expandedMessages : []}
                        expandedDraft={expandedTicketId === t.id ? expandedDraft : ''}
                        onChangeDraft={setExpandedDraft}
                        onSendReply={handleSendReply}
                        documents={documents}
                        onOpenSopDocument={onOpenSopDocument}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {archivedResolved.length > 0 && (
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setResolvedArchiveCollapsed((v) => !v)}
                aria-expanded={!resolvedArchiveCollapsed}
                className={`w-full px-3 py-2 ${resolvedArchiveCollapsed ? '' : 'border-b'} text-sm font-semibold flex items-center justify-between text-left bg-gray-50 text-gray-700 hover:bg-gray-100`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <svg
                    className={`h-4 w-4 shrink-0 text-current transform transition-transform ${resolvedArchiveCollapsed ? '-rotate-90' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                  <span className="truncate">Resolved Tickets</span>
                  <span className="text-[11px] font-normal text-gray-500">{archivedResolved.length}</span>
                </div>
              </button>
              {!resolvedArchiveCollapsed && (
                <div>
                  <div className="px-3 py-2 border-b bg-white">
                    <div className="relative">
                      <input
                        type="search"
                        value={resolvedSearch}
                        onChange={(e) => setResolvedSearch(e.target.value)}
                        placeholder="Search resolved tickets…"
                        className="w-full text-sm border rounded-lg pl-8 pr-8 py-1.5 focus:outline-none focus:ring-2 focus:ring-teal-300"
                        aria-label="Search resolved tickets"
                      />
                      <svg className="h-4 w-4 absolute left-2 top-2 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      {resolvedSearch && (
                        <button
                          type="button"
                          onClick={() => setResolvedSearch('')}
                          className="absolute right-2 top-1.5 text-gray-400 hover:text-gray-700 text-xs"
                          aria-label="Clear search"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    {resolvedSearch && (
                      <div className="text-[11px] text-gray-500 mt-1">
                        {visibleArchivedResolved.length} match{visibleArchivedResolved.length === 1 ? '' : 'es'} of {archivedResolved.length}
                      </div>
                    )}
                  </div>
                  {visibleArchivedResolved.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500 text-center">
                      No resolved tickets match "{resolvedSearch}".
                    </div>
                  ) : (
                    <div className="divide-y">
                      {visibleArchivedResolved.map((t) => (
                        <TicketRow
                          key={t.id}
                          t={t}
                          isNew={false}
                          expanded={expandedTicketId === t.id}
                          onToggle={handleToggleExpand}
                          expandedLoading={expandedTicketId === t.id ? expandedLoading : false}
                          expandedMessages={expandedTicketId === t.id ? expandedMessages : []}
                          expandedDraft={expandedTicketId === t.id ? expandedDraft : ''}
                          onChangeDraft={setExpandedDraft}
                          onSendReply={handleSendReply}
                          documents={documents}
                          onOpenSopDocument={onOpenSopDocument}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
